import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Radio,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useAuth } from "../../hooks/useAuth";
import { useConfirmInsufficientTissue } from "../../hooks/useWorkflows";
import {
  insufficientTissueService,
  insufficientTissueError,
  InsufficientTissuePreview,
  InsufficientTissueSpecimen,
} from "../../services/workflows.service";

interface Props {
  caseId: string;
  specimen: InsufficientTissueSpecimen;
  sourceName: string;
  disabled?: boolean;
}

export function InsufficientTissueConfirmationModal({
  caseId,
  specimen,
  sourceName,
  disabled,
}: Props) {
  const { user } = useAuth();
  const mutation = useConfirmInsufficientTissue();
  const [opened, setOpened] = useState(false);
  const [preview, setPreview] = useState<InsufficientTissuePreview>();
  const [previewError, setPreviewError] = useState<string>();
  const [requestId, setRequestId] = useState("");
  const form = useForm<{ mode: "" | "all" | "he_only" }>({
    initialValues: { mode: "" },
    validateInputOnBlur: true,
    validate: {
      mode: (value) => (value ? null : "Choose which stains to remove."),
    },
  });
  const allowed = useMemo(
    () =>
      user?.permissions?.includes("cutting.write") &&
      user.permissions.some((permission: string) =>
        ["stains.delete", "pathologist_review.stain_delete"].includes(
          permission
        )
      ),
    [user]
  );
  const handleOpen = useCallback(async () => {
    form.reset();
    mutation.reset();
    setPreview(undefined);
    setPreviewError(undefined);
    setRequestId(crypto.randomUUID());
    setOpened(true);
    try {
      setPreview(
        await insufficientTissueService.preview({
          caseId,
          specimenId: specimen.id,
          stage: "cutting",
        })
      );
    } catch (error) {
      setPreviewError(insufficientTissueError(error));
    }
  }, [caseId, specimen.id, form, mutation]);
  const handleClose = useCallback(() => {
    if (!mutation.isPending) setOpened(false);
  }, [mutation.isPending]);
  const handleModeChange = useCallback(
    (value: string) => {
      form.setFieldValue("mode", value as "all" | "he_only");
    },
    [form]
  );
  const handleConfirm = useCallback(async () => {
    if (form.validate().hasErrors || !preview || !form.values.mode) return;
    try {
      const result = await mutation.mutateAsync({
        caseId,
        specimenId: specimen.id,
        stage: "cutting",
        mode: form.values.mode,
        expectedVersion: preview.version,
        materialFingerprint: preview.materialFingerprint,
        requestId,
      });
      setOpened(false);
      notifications.show({
        title: "Insufficient tissue recorded",
        message: `${result.removedSlideIds.length} stain slide(s) removed. Workflow updated to ${result.currentStatus}.`,
        color: "green",
      });
    } catch {
      /* The mutation error is displayed inside the confirmation dialog. */
    }
  }, [caseId, specimen.id, form, preview, requestId, mutation]);
  const error = useMemo(
    () =>
      previewError ||
      (mutation.error ? insufficientTissueError(mutation.error) : undefined),
    [previewError, mutation.error]
  );
  return (
    <>
      <Stack gap={6} align="flex-start">
        <Text fw={600} size="md">
          Specimen {specimen.number} - {sourceName}
        </Text>
        <Button
          variant="outline"
          color="orange.8"
          size="xs"
          onClick={handleOpen}
          disabled={disabled || !allowed}
          aria-label={`Resolve insufficient tissue — Specimen ${specimen.number}`}
        >
          Resolve insufficient tissue
        </Button>
      </Stack>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={`Insufficient tissue — Specimen ${specimen.number}`}
        centered
        withinPortal
        closeOnClickOutside={!mutation.isPending}
        closeOnEscape={!mutation.isPending}
      >
        <Stack gap="md">
          <Text size="sm">
            Remove stain slides only from this specimen. Existing printed-label
            and clinical audit history is preserved.
          </Text>
          {!preview && !error && (
            <Loader aria-label="Loading specimen material" />
          )}
          {preview && (
            <>
              <Text size="sm">
                {preview.slideCount} active stain slide(s), including{" "}
                {preview.heCount} H&amp;E.
              </Text>
              <Radio.Group
                label="Choose the remaining stains"
                value={form.values.mode}
                onChange={handleModeChange}
                error={form.errors.mode}
              >
                <Stack gap="sm" mt="xs">
                  <Radio
                    value="all"
                    label="Delete all stains (including H&E)"
                    disabled={mutation.isPending}
                  />
                  <Radio
                    value="he_only"
                    label="H&E only — remove other stains and retain existing H&E"
                    disabled={mutation.isPending}
                  />
                </Stack>
              </Radio.Group>
              <Text size="sm" c="dimmed">
                If H&amp;E is absent, no H&amp;E slide will be created.
              </Text>
            </>
          )}
          {error && (
            <Alert
              color="red"
              variant="light"
              title="Could not remove stains"
              role="alert"
            >
              {error}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={handleClose}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleConfirm}
              loading={mutation.isPending}
              disabled={!preview || !!previewError}
            >
              Confirm removal
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
